import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamation } from "@fortawesome/free-solid-svg-icons";
import "../../admin-lo-listing-calendar.scss";

export default function CalendarCell({
  day,
  currentMonthIndex,
  dayIndex,
  dayReservations = [],
  onClick,
}) {
  const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
  const isCurrentMonth = day.getMonth() === currentMonthIndex;
  const hasAnyReservation = dayReservations.length > 0;
  const isWeekend = dayIndex === 5 || dayIndex === 6;

  // helper to normalize status from different shapes
  const getStatus = (r) => {
    const s = (r.listingDateStatus || r.status || r.raw?.status || "").toString().toLowerCase();
    return s;
  };

  const hasApproved =
    hasAnyReservation &&
    dayReservations.some((r) => getStatus(r) === "approved");

  // true when every item for the date is blocked
  const allBlocked =
    hasAnyReservation &&
    dayReservations.every((r) => getStatus(r) === "blocked");

  // whether there's at least one blocked item (mixed case)
  const hasBlocked =
    hasAnyReservation &&
    dayReservations.some((r) => getStatus(r) === "blocked");

  // whether any blocked item is marked as holiday (support both isHoliday and legacy holiday)
  const hasHoliday =
    hasAnyReservation &&
    dayReservations.some((r) => {
      return Boolean(
        r.isHoliday === true ||
        r.raw?.isHoliday === true ||
        r.raw?.holiday === true ||
        r.is_holiday === true
      );
    });

  // A day is blockable when it has no reservations
  const canBlock = !hasAnyReservation && !isWeekend;

  // clickable only when NOT weekend and either has reservations or can be blocked
  const isClickable = !isWeekend && (hasAnyReservation || canBlock);

  const cellClassNames = [
    "adminlo-calendar-cell",
    !isCurrentMonth && "adminlo-calendar-cell--offmonth",
    hasAnyReservation && "adminlo-calendar-cell--has-reservation",
    hasApproved && "adminlo-calendar-cell--approved",
    hasBlocked && "adminlo-calendar-cell--blocked",
    hasHoliday && "adminlo-calendar-cell--holiday",
    // disable weekends only (don't automatically disable blocked — still clickable to view read-only modal)
    isWeekend && "adminlo-calendar-cell--disabled",
    canBlock && "adminlo-calendar-cell--blockable",
    isClickable && "adminlo-calendar-cell--clickable",
  ]
    .filter(Boolean)
    .join(" ");

  // Build the institutions list but only show the first/only one
  const institutions = dayReservations
    .map((r) => {
      if (getStatus(r) === "blocked") {
        return r.notes || r.reason || r.raw?.notes || r.raw?.reason || "(blocked)";
      }
      return r.institution || r.raw?.institution || "(no name)";
    })
    .filter(Boolean);

  const firstInstitution = institutions[0] || "(no name)";

  // show the exclamation only when there are reservations (not when all items are blocked)
  const showWarningBadge = hasAnyReservation && !hasApproved && !isWeekend && !allBlocked;

  return (
    <td
      key={dayKey}
      className={cellClassNames}
      onClick={() => {
        if (!isClickable) return;
        onClick && onClick(day);
      }}
      role={isClickable ? "button" : undefined}
      aria-disabled={!isClickable}
    >
      <div className="cell-top-row">
        <div className="cell-day-number">{day.getDate()}</div>

        {showWarningBadge && (
          <div className="cell-badge-count" title="Pending reservation(s)">
            <FontAwesomeIcon icon={faExclamation} />
          </div>
        )}
      </div>

      {hasAnyReservation && (
        <div className="cell-institutions">
          <div
            className="cell-institution-row"
            title={firstInstitution}
            aria-label={`Reservation by ${firstInstitution}`}
          >
            {firstInstitution}
          </div>
        </div>
      )}
    </td>
  );
}
